import React, { FC } from 'react';
import { Alert, Badge } from 'reactstrap';
import {
  getReleasePeriod,
  PublicHotelReleaseGroup,
  ReleaseForceEntry,
  ReleaseGroupStage,
} from '../../../../shared/hotel';
import {
  CardListItemAction,
  CardListItemElement,
  formatDate,
  RichMarkdownIt,
} from '../../../components';
import { useTranslation } from '../../../translations';
import { displayName } from '../../../utils';

interface Props {
  readonly wave: PublicHotelReleaseGroup;
}

export const RandomListItem: FC<Props> = ({ wave }) => {
  const now = new Date();
  const { ts } = useTranslation();

  const actions: CardListItemAction[] = [];
  const period = getReleasePeriod(wave, now);

  if (wave.forceEntry === ReleaseForceEntry.None && period === ReleaseGroupStage.EntryPeriod) {
    actions.push({
      text: 'hotel.wave.enter_btn',
      color: 'primary',
      id: 'enter-queue',
      to: `/hotel/wave/${wave.id}`,
    });
  } else {
    actions.push({
      text: 'hotel.wave.view_btn',
      color: 'secondary',
      id: 'view-wave',
      to: `/hotel/wave/${wave.id}`,
    });
  }

  return (
    <CardListItemElement
      actions={actions}
      badges={<GetBadges period={period} wave={wave} />}
      description={
        <>
          <RichMarkdownIt className="text-truncate d-block" source={wave.description} />
          <GetAlerts period={period} wave={wave} />
        </>
      }
      id={wave.id}
      subtitle={
        <ul className="list-unstyled">
          <li>
            <strong>{ts('entry_period')}</strong>: {formatDate(wave.entryStartAt!)} &mdash;{' '}
            {formatDate(wave.entryEndAt!)}
          </li>
          <li>
            <strong>{ts('reservation_period')}</strong>: {formatDate(wave.releaseStartAt)} &mdash;{' '}
            {formatDate(wave.releaseEndAt)}
          </li>
        </ul>
      }
      title={displayName(wave)}
    />
  );
};

interface SubProps extends Props {
  readonly period: ReleaseGroupStage;
}

const GetBadges: FC<SubProps> = ({ wave, period }) => {
  const { ts } = useTranslation();
  const badges: React.JSX.Element[] = [];

  badges.push(
    <Badge color="secondary" key="random_draw">
      {ts('random_draw')}
    </Badge>,
  );

  switch (period) {
    case ReleaseGroupStage.PreEntry: {
      badges.push(
        <Badge color="warning" key="not_yet_open">
          {ts('not_yet_open')}
        </Badge>,
        <Badge color="info" key="entries_open_date">
          {ts('entries_open')}
          {formatDate(wave.releaseStartAt)}
        </Badge>,
      );

      break;
    }

    case ReleaseGroupStage.EntryPeriod: {
      badges.push(
        <Badge color="success" key="entries_open">
          {ts('entries_open')}
        </Badge>,
        <Badge color="info" key="entries_close">
          {ts('entries_close')} {formatDate(wave.entryEndAt!)}
        </Badge>,
      );

      break;
    }

    case ReleaseGroupStage.PreReleaseReady: {
      badges.push(
        <Badge color="info" key="draw_in_progress">
          {ts('draw_in_progress')}
        </Badge>,
        <Badge color="info" key="results_available">
          {ts('results_available_on')}
          {formatDate(wave.releaseStartAt)}
        </Badge>,
      );

      break;
    }

    case ReleaseGroupStage.ReleasePeriod: {
      badges.push(
        <Badge color="success" key="results_available">
          {ts('results_available')}
        </Badge>,
        <Badge color="secondary" key="booking_window_ends">
          {ts('booking_window_ends')} {formatDate(wave.releaseEndAt)}
        </Badge>,
      );

      break;
    }

    case ReleaseGroupStage.Finished: {
      badges.push(
        <Badge color="danger" key="finished">
          {ts('finished')}
        </Badge>,
      );

      break;
    }
  }

  return badges;
};

const GetAlerts: FC<SubProps> = ({ wave, period }) => {
  const { ts } = useTranslation();
  const alerts: React.JSX.Element[] = [];

  switch (period) {
    case ReleaseGroupStage.PreEntry: {
      alerts.push(
        <Alert color="info" fade={false} key="wave_begins_entry">
          {ts('this_wave_will_begin_accepting_entries_on')}
          {formatDate(wave.releaseStartAt)}.
        </Alert>,
      );

      // caution: break intentionally omitted -- PreEntry requires the additional alerts of EntryPeriod
    }

    case ReleaseGroupStage.EntryPeriod: {
      if (wave.eligibility.isEligible) {
        alerts.push(
          <Alert color="success" fade={false} id="hotel_eligible" key="eligible">
            {ts('you_are_eligible_to_enter_this_wave')}
          </Alert>,
        );
      } else {
        alerts.push(
          <Alert color="danger" fade={false} id="hotel_not_eligible" key="not_eligible">
            {ts('you_are_not_eligible_to_enter_this_wave')}
          </Alert>,
        );
      }

      break;
    }

    case ReleaseGroupStage.PreReleaseReady: {
      alerts.push(
        <Alert color="info" fade={false} key="results_availability">
          <p>
            {ts('results_will_be_available_on')}
            {formatDate(wave.releaseStartAt)}.
          </p>
        </Alert>,
      );

      break;
    }

    case ReleaseGroupStage.Finished: {
      alerts.push(
        <div key="finishedAlert">
          <hr />
          <p className="text-muted mt-2">{ts('hotel.release_has_finished')}</p>
        </div>,
      );

      break;
    }

    default: {
      // No alerts for other periods
      break;
    }
  }

  return alerts;
};
